import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '@/context/AuthContext';
import { useAcceptInvite } from '@/lib/queries/groups';
import { logEvent } from '@/lib/analytics';
import theme from '@/constants/colors';
import { useTranslation } from 'react-i18next';

const PENDING_KEY = 'pendingInviteCode';

export default function JoinScreen() {
  const { t } = useTranslation();
  const { code } = useLocalSearchParams<{ code: string }>();
  const router = useRouter();
  const { session, isLoading } = useAuth();
  const token = session?.accessToken;
  const accept = useAcceptInvite(token);
  const [status, setStatus] = useState<'loading' | 'redirecting'>('loading');

  useEffect(() => {
    if (isLoading) return;
    const codeStr = typeof code === 'string' ? code : undefined;

    // No auth yet — stash the code and send to login.
    if (!token) {
      if (codeStr) AsyncStorage.setItem(PENDING_KEY, codeStr).catch(() => {});
      setStatus('redirecting');
      router.replace('/login');
      return;
    }

    if (!codeStr) {
      // No code — nothing to do, go home.
      router.replace('/(tabs)/groups' as any);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await accept.mutateAsync(codeStr);
        logEvent('group_joined', { via: 'link' });
      } catch (e: any) {
        // 409 / "already accepted" (auto-redeemed on login) → treat as success
        if (!/already|409|accepted/i.test(e?.message || '')) {
          // Real failure — go to groups anyway
        }
      }
      if (cancelled) return;
      await AsyncStorage.removeItem(PENDING_KEY).catch(() => {});
      router.replace('/(tabs)/groups' as any);
    })();
    return () => { cancelled = true; };
  }, [isLoading, token, code]);

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color={theme.accentPrimary} />
      <Text style={styles.text}>{t('groups.joining')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.backgroundPrimary, gap: 16 },
  text: { color: theme.textMuted, fontSize: 16 },
});